import type { MovieMark } from '@zyx/shared';
import { Bookmark, BookmarkCheck, Eye, EyeOff, Heart, ListPlus, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useUpdateMark } from '@/hooks/use-movies';
import { cn } from '@/lib/utils';
import { ListMenu } from './list-menu';

/** Watchlist, lists, favourite, a 1–5 rating and hide, for one movie. */
export function MarkControls(props: { readonly code: string; readonly mark: MovieMark }) {
  const { mark } = props;
  const update = useUpdateMark(props.code);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button variant={mark.inWatchlist ? 'default' : 'outline'} onClick={() => update.mutate({ inWatchlist: !mark.inWatchlist })} disabled={update.isPending}>
        {mark.inWatchlist ? <BookmarkCheck className="size-4" /> : <Bookmark className="size-4" />}
        {mark.inWatchlist ? 'In watchlist' : 'Watchlist'}
      </Button>
      <ListMenu code={props.code} listIds={mark.listIds ?? []} align="start">
        <Button variant="outline">
          <ListPlus className="size-4" />
          {(mark.listIds?.length ?? 0) === 0 ? 'Lists' : `On ${mark.listIds.length} list${mark.listIds.length === 1 ? '' : 's'}`}
        </Button>
      </ListMenu>
      <Button variant="outline" onClick={() => update.mutate({ favourite: !mark.favourite })} disabled={update.isPending} aria-pressed={mark.favourite}>
        <Heart className={cn('size-4', mark.favourite && 'fill-primary text-primary')} />
        Favourite
      </Button>
      <div className="flex items-center rounded-md border border-input px-1.5" role="radiogroup" aria-label="Rating">
        {[1, 2, 3, 4, 5].map((value) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={mark.rating === value}
            aria-label={`${value} star${value === 1 ? '' : 's'}`}
            disabled={update.isPending}
            // Choosing the current rating again clears it.
            onClick={() => update.mutate({ rating: mark.rating === value ? null : value })}
            className="rounded p-1 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <Star className={cn('size-4', (mark.rating ?? 0) >= value ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground')} />
          </button>
        ))}
      </div>
      <Button variant="ghost" onClick={() => update.mutate({ hidden: !mark.hidden })} disabled={update.isPending}>
        {mark.hidden ? <Eye className="size-4" /> : <EyeOff className="size-4" />}
        {mark.hidden ? 'Unhide' : 'Hide'}
      </Button>
    </div>
  );
}
